import { useScrollReveal } from '@/hooks/useScrollReveal';

const WHATSAPP_NUMBER = '+923348872555';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}`;

const highlights = [
  {
    icon: 'ri-money-dollar-circle-line',
    label: 'Payment Mode',
    value: 'Cash Only',
    desc: 'Full payment at time of booking',
    color: '#d4853a',
  },
  {
    icon: 'ri-key-2-line',
    label: 'Possession',
    value: 'Ready Now',
    desc: 'Immediate plot possession on payment',
    color: '#4a7c59',
  },
  {
    icon: 'ri-file-transfer-line',
    label: 'Ownership Transfer',
    value: 'Instant',
    desc: 'Full legal transfer upon payment',
    color: '#d4853a',
  },
  {
    icon: 'ri-shield-check-line',
    label: 'Documentation',
    value: 'Complete',
    desc: 'All legal docs provided at possession',
    color: '#4a7c59',
  },
];

const priceOptions = [
  { size: '5 Marla', price: 'PKR 1,700,000', icon: 'ri-layout-grid-line' },
  { size: '10 Marla', price: 'PKR 3,000,000', icon: 'ri-layout-masonry-line' },
  { size: '1 Kanal', price: 'PKR 4,200,000', icon: 'ri-building-3-line' },
];

export default function PaymentPlan() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section ref={ref} className="w-full py-16 md:py-24 bg-[#111] relative overflow-hidden">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}
      />
      {/* Subtle gradient accents */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#d4853a]/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#4a7c59]/8 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-10 lg:px-16">
        {/* Header */}
        <div className={`text-center mb-12 md:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-[11px] font-bold text-[#d4853a] uppercase tracking-[0.2em] mb-3">
            Investment Terms
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-[2.25rem] font-extrabold text-white leading-tight mb-4">
            Cash Payment &amp; <span className="text-[#d4853a]">Immediate Possession</span>
          </h2>
          <p className="text-[15px] text-white/50 max-w-xl mx-auto leading-relaxed">
            No waiting. No installments. Secure your plot with a single cash payment and receive
            full possession and legal documentation on the same day.
          </p>
        </div>

        {/* Cash Only Banner */}
        <div className={`mb-10 md:mb-12 transition-all duration-700 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="relative flex items-center justify-center gap-4 md:gap-6 py-5 md:py-6 px-6 rounded-2xl border border-[#d4853a]/20 bg-[#d4853a]/5 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#d4853a]/5 via-transparent to-[#d4853a]/5" />
            <div className="relative flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#d4853a]/15 text-[#d4853a] shrink-0">
                <i className="ri-currency-line text-xl" />
              </div>
              <div>
                <div className="text-white font-extrabold text-lg md:text-xl tracking-tight">Cash Only Offer</div>
                <div className="text-white/40 text-[12px]">No installment plan available for this project</div>
              </div>
            </div>
            <div className="hidden md:block w-px h-12 bg-white/[0.08]" />
            <div className="relative hidden md:flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#4a7c59]/15 text-[#4a7c59] shrink-0">
                <i className="ri-home-4-line text-xl" />
              </div>
              <div>
                <div className="text-white font-extrabold text-lg md:text-xl tracking-tight">Possession Ready</div>
                <div className="text-white/40 text-[12px]">Immediate handover on payment completion</div>
              </div>
            </div>
          </div>
        </div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-12 md:mb-16">
          {highlights.map((item, i) => (
            <div
              key={item.label}
              className={`group relative bg-white/[0.04] backdrop-blur-sm rounded-2xl p-5 md:p-6 border border-white/[0.06] hover:border-[${item.color}]/20 transition-all duration-500 hover:-translate-y-1 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: isVisible ? `${i * 100 + 200}ms` : '0ms' }}
            >
              <div
                className="w-12 h-12 flex items-center justify-center rounded-xl mb-4 transition-all duration-300"
                style={{ backgroundColor: `${item.color}15`, color: item.color }}
              >
                <i className={`${item.icon} text-xl`} />
              </div>
              <div className="text-[11px] text-white/30 uppercase tracking-wider font-medium mb-1">{item.label}</div>
              <div className="text-xl font-extrabold text-white mb-1" style={{ color: item.color }}>{item.value}</div>
              <div className="text-[12px] text-white/40 leading-relaxed">{item.desc}</div>
            </div>
          ))}
        </div>

        {/* Pricing Summary */}
        <div className={`transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="text-center mb-7">
            <div className="text-[11px] font-bold text-white/30 uppercase tracking-[0.2em]">All-In Cash Prices</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
            {priceOptions.map((opt, i) => (
              <div
                key={opt.size}
                className={`relative group bg-white/[0.03] rounded-2xl p-6 border border-white/[0.06] hover:border-[#d4853a]/25 transition-all duration-400 hover:-translate-y-1 text-center ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: isVisible ? `${i * 100 + 550}ms` : '0ms' }}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#d4853a]/10 text-[#d4853a] mb-4 mx-auto transition-all duration-300 group-hover:bg-[#d4853a] group-hover:text-white">
                  <i className={`${opt.icon} text-lg`} />
                </div>
                <div className="text-lg font-bold text-white mb-1">{opt.size}</div>
                <div className="text-2xl font-extrabold text-[#d4853a] mb-3">{opt.price}</div>
                <div className="flex items-center justify-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4a7c59] animate-pulse" />
                  <span className="text-[11px] font-semibold text-[#4a7c59]">Available</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={`mt-12 md:mt-14 text-center transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-[13px] text-white/30 mb-5">
            Ready to invest? Contact us to confirm availability and proceed with your cash booking.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#d4853a] text-white text-[13px] font-bold rounded-xl transition-all duration-300 hover:bg-[#c27630] hover:shadow-[0_12px_40px_rgba(212,133,58,0.35)] hover:-translate-y-0.5 whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-[17px]" />
              WhatsApp to Book Now
            </a>
            <a
              href={`tel:${WHATSAPP_NUMBER}`}
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white/[0.06] text-white text-[13px] font-semibold rounded-xl border border-white/[0.08] transition-all duration-300 hover:bg-white/[0.1] hover:-translate-y-0.5 whitespace-nowrap"
            >
              <i className="ri-phone-line text-[15px]" />
              Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}